import { useId, useState } from 'react';

import { formatSubmissionDeadline, WEEKDAY_NAMES } from '@time-tracking/shared';
import type { SubmissionDeadlineDto } from '@time-tracking/shared';

import { useSubmissionDeadline, useUpdateSubmissionDeadline } from '../../../api/admin.js';

/** "17:05" from {hour:17, minute:5} — the value an `<input type="time">` expects. */
function toTimeValue(d: SubmissionDeadlineDto): string {
  return `${String(d.hour).padStart(2, '0')}:${String(d.minute).padStart(2, '0')}`;
}

/** Returns null for a cleared or half-typed field rather than coercing to midnight. */
function parseTimeValue(value: string): { hour: number; minute: number } | null {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  if (!m?.[1] || !m[2]) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/**
 * The administrator's control for the submission deadline (R14, `I-01`).
 *
 * The rule was already a stored setting with a read/write API, but nothing in the UI
 * called it — so in practice it was a code constant that happened to live in a table,
 * which is exactly what R14 exists to prevent. `I-01` asks for settings to be reachable
 * from the admin UI *and* the API, not either.
 *
 * Worth knowing before changing this: the deadline is global and applies retroactively.
 * Every historical week's `daysPastDue` is recomputed against whatever this says now, so
 * moving it relabels how late named colleagues already are. The copy says so, and the
 * mutation invalidates the approvals queries so the relabelling is immediate rather than
 * appearing later as drift.
 */
export function SubmissionDeadlineCard() {
  const deadline = useSubmissionDeadline();
  const update = useUpdateSubmissionDeadline();
  const dayId = useId();
  const timeId = useId();

  // Local edits only; the server value is the base. Derived during render rather than
  // seeded by an effect, which fixed two defects at once: the effect ran a render LATE,
  // so the very first render after data arrived still had `draft === null` and fell into
  // the "Couldn't load" branch below — flashing a false error on every successful load —
  // and it re-seeded on every payload change, silently discarding an admin's in-progress
  // edit the moment a co-admin saved.
  const [edit, setEdit] = useState<SubmissionDeadlineDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const server = deadline.data ?? null;
  const draft = edit ?? server;

  if (deadline.isPending) {
    return (
      <section className="rp-deadline" aria-busy="true">
        <h3>When hours are due</h3>
        <p className="rp-deadline__loading">Loading the current rule…</p>
      </section>
    );
  }

  // Only a genuine load failure, not the transient no-value-yet state.
  if (deadline.isError && draft === null) {
    return (
      <section className="rp-deadline">
        <h3>When hours are due</h3>
        <div className="error-banner" role="alert">
          <p>Couldn&rsquo;t load the current deadline.</p>
          <button
            type="button"
            className="btn btn--sm btn--light"
            onClick={() => void deadline.refetch()}
          >
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (draft === null) return null;
  const current = server;
  const dirty =
    current !== null &&
    (draft.dayOfWeek !== current.dayOfWeek ||
      draft.hour !== current.hour ||
      draft.minute !== current.minute);

  function handleSave() {
    if (!draft) return;
    setError(null);
    setSaved(false);
    const submitted = draft;
    update.mutate(submitted, {
      // Drop the local edit so the card re-derives from the server value it just wrote —
      // and so a later concurrent change is no longer competing with a stale draft.
      //
      // Only if it is still the edit that was sent. The controls stay live during the
      // request, so an administrator can change the day or time again while it is in
      // flight; clearing unconditionally threw that newer edit away and snapped the form
      // back to the value they had already moved on from. Same failure the render-derived
      // draft was introduced to fix, on the success path rather than the refetch path.
      onSuccess: () => {
        setEdit((current) =>
          current !== null &&
          current.dayOfWeek === submitted.dayOfWeek &&
          current.hour === submitted.hour &&
          current.minute === submitted.minute
            ? null
            : current,
        );
        setSaved(true);
      },
      onError: (e) => setError(e.message || 'Couldn’t save the deadline. Please try again.'),
    });
  }

  return (
    <section className="rp-deadline">
      <h3>When hours are due</h3>
      <p className="rp-deadline__intro">
        Timesheets are due <strong>{formatSubmissionDeadline(current ?? draft)}</strong>, in Central
        Time. A week joins the Needs-action count the moment that deadline passes; the &ldquo;days
        past due&rdquo; label and the Past-due filter advance the next calendar day, so nobody is
        shown as late on their own due date. The Needs-action page states the rule so the people it
        names can read it too.
      </p>

      <div className="rp-deadline__fields">
        <div className="rp-deadline__field">
          <label htmlFor={dayId}>Day</label>
          <select
            id={dayId}
            value={draft.dayOfWeek}
            onChange={(e) => setEdit({ ...draft, dayOfWeek: Number(e.target.value) })}
          >
            {WEEKDAY_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="rp-deadline__field">
          <label htmlFor={timeId}>Time</label>
          <input
            id={timeId}
            type="time"
            value={toTimeValue(draft)}
            onChange={(e) => {
              const parsed = parseTimeValue(e.target.value);
              // Ignore a half-typed or cleared field rather than snapping to 00:00,
              // which would silently make every week due at midnight.
              if (parsed) setEdit({ ...draft, ...parsed });
            }}
          />
        </div>

        <button
          type="button"
          className="btn primary"
          disabled={!dirty || update.isPending}
          onClick={handleSave}
        >
          {update.isPending ? 'Saving…' : 'Save deadline'}
        </button>
      </div>

      <p className="rp-deadline__warning">
        Changing this is retroactive: every past week&rsquo;s &ldquo;days past due&rdquo; is
        measured against the rule in force now, so moving the deadline changes how late people
        already appear.
      </p>

      {/* role="status" carries it to assistive tech; the visible text is for everyone
          else. An sr-only-only confirmation left a sighted admin with nothing but the
          Save button greying out to tell them a retroactive change had landed. */}
      <p className="rp-deadline__saved" role="status">
        {saved && !dirty ? `Saved. Hours are due ${formatSubmissionDeadline(draft)}.` : ''}
      </p>
      {error !== null && (
        <div className="error-banner" role="alert">
          <p>{error}</p>
        </div>
      )}
    </section>
  );
}
